import type { ImportedNode, ImportedTree } from './types';

/**
 * Parse a FreeMind .mm XML string into an ImportedTree.
 *
 * FreeMind files use `<node TEXT="...">` elements nested to represent
 * the tree hierarchy. Attributes handled:
 * - TEXT: the node label
 * - FOLDED: whether the node was collapsed (ignored in import, kept in notes)
 * - LINK: a URL associated with the node (appended to text)
 *
 * Rich content notes are extracted from `<richcontent>` child elements.
 */
export function parseFreeMind(xmlString: string): ImportedTree {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  const errorNode = xmlDoc.querySelector('parsererror');
  if (errorNode) {
    throw new Error(`Invalid FreeMind XML: ${errorNode.textContent ?? 'parse error'}`);
  }

  const mapElement = xmlDoc.querySelector('map');
  if (!mapElement) {
    throw new Error('Invalid FreeMind file: missing <map> root element');
  }

  const rootNodeElement = mapElement.querySelector(':scope > node');
  if (!rootNodeElement) {
    throw new Error('Invalid FreeMind file: missing root <node> element');
  }

  return { root: convertElement(rootNodeElement) };
}

function convertElement(element: Element): ImportedNode {
  const text = buildNodeText(element);
  const notes = extractNotes(element);

  const children: ImportedNode[] = [];
  for (const child of element.children) {
    if (child.tagName === 'node') {
      children.push(convertElement(child));
    }
  }

  const node: ImportedNode = { text, children };
  if (notes) {
    node.notes = notes;
  }
  return node;
}

function buildNodeText(element: Element): string {
  let text = element.getAttribute('TEXT') ?? '';
  const link = element.getAttribute('LINK');
  if (link) {
    text = text ? `${text} (${link})` : link;
  }
  return text;
}

function extractNotes(element: Element): string | undefined {
  const richContentElements = element.querySelectorAll(':scope > richcontent');
  const parts: string[] = [];

  for (const rc of richContentElements) {
    const type = rc.getAttribute('TYPE');
    // NOTE type is the standard notes richcontent; also accept DETAILS
    if (type === 'NOTE' || type === 'DETAILS') {
      const textContent = rc.textContent?.trim();
      if (textContent) {
        parts.push(textContent);
      }
    }
  }

  return parts.length > 0 ? parts.join('\n') : undefined;
}
